import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';

import { ErrorBoundary } from './ErrorBoundary';
import { RouteFallback } from './RouteFallback';
import { Shell } from './Shell';

function page(Component: LazyExoticComponent<ComponentType>) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Component />
      </Suspense>
    </ErrorBoundary>
  );
}

const NotFound = lazy(() => import('./NotFound'));
const CatalogPage = lazy(() => import('@/features/catalog/CatalogPage'));
const TripDetailPage = lazy(() => import('@/features/catalog/TripDetailPage'));
const PastTripsPage = lazy(() => import('@/features/catalog/PastTripsPage'));
const RegistrationPage = lazy(() => import('@/features/booking/RegistrationPage'));
const PaymentPage = lazy(() => import('@/features/payments/PaymentPage'));
const PaystackHandoffPage = lazy(() => import('@/features/payments/PaystackHandoffPage'));
const PayReturnPage = lazy(() => import('@/features/payments/PayReturnPage'));
const TransferPage = lazy(() => import('@/features/payments/TransferPage'));
const WhatsAppPage = lazy(() => import('@/features/payments/WhatsAppPage'));
const ConfirmationPage = lazy(() => import('@/features/booking/ConfirmationPage'));
const BookingPage = lazy(() => import('@/features/manage/BookingPage'));
const SignInPage = lazy(() => import('@/features/auth/SignInPage'));
const CreateAccountPage = lazy(() => import('@/features/auth/CreateAccountPage'));
const FindBookingPage = lazy(() => import('@/features/auth/FindBookingPage'));
const LinkBookingPage = lazy(() => import('@/features/auth/LinkBookingPage'));
const AccountPage = lazy(() => import('@/features/account/AccountPage'));
const NotificationsPage = lazy(() => import('@/features/account/NotificationsPage'));
const DeleteAccountPage = lazy(() => import('@/features/account/DeleteAccountPage'));
const TermsPage = lazy(() => import('@/features/policies').then((m) => ({ default: m.TermsPage })));
const RefundsPage = lazy(() =>
  import('@/features/policies').then((m) => ({ default: m.RefundsPage })),
);
const PrivacyPage = lazy(() =>
  import('@/features/policies').then((m) => ({ default: m.PrivacyPage })),
);
const WaitlistOffersPage = lazy(() => import('@/features/waitlist/WaitlistOffersPage'));
const AcceptWaitlistOfferPage = lazy(() => import('@/features/waitlist/AcceptWaitlistOfferPage'));
const BalancePage = lazy(() => import('@/features/manage/BalancePage'));
const CompanionsPage = lazy(() => import('@/features/manage/CompanionsPage'));
const EmergencyContactPage = lazy(() => import('@/features/manage/EmergencyContactPage'));
const CancelPage = lazy(() => import('@/features/manage/CancelPage'));

export const routes: RouteObject[] = [
  {
    element: <Shell />,
    children: [
      { index: true, element: page(CatalogPage) },
      { path: 'trips/:slug', element: page(TripDetailPage) },
      { path: 'trips/:slug/book', element: page(RegistrationPage) },
      { path: 'bookings/:reference', element: page(BookingPage) },
      { path: 'bookings/:reference/pay', element: page(PaymentPage) },
      { path: 'bookings/:reference/pay-balance', element: page(BalancePage) },
      { path: 'bookings/:reference/companions', element: page(CompanionsPage) },
      { path: 'bookings/:reference/emergency-contact', element: page(EmergencyContactPage) },
      { path: 'bookings/:reference/cancel', element: page(CancelPage) },
      { path: 'bookings/:reference/pay/paystack', element: page(PaystackHandoffPage) },
      { path: 'bookings/:reference/pay/transfer', element: page(TransferPage) },
      { path: 'bookings/:reference/pay/whatsapp', element: page(WhatsAppPage) },
      { path: 'bookings/:reference/confirmed', element: page(ConfirmationPage) },
      { path: 'waitlist', element: page(WaitlistOffersPage) },
      { path: 'waitlist/accept', element: page(AcceptWaitlistOfferPage) },
      { path: 'bookings/:reference/link', element: page(LinkBookingPage) },
      { path: 'pay/return', element: page(PayReturnPage) },
      { path: 'sign-in', element: page(SignInPage) },
      { path: 'create-account', element: page(CreateAccountPage) },
      { path: 'find-booking', element: page(FindBookingPage) },
      { path: 'account', element: page(AccountPage) },
      { path: 'account/notifications', element: page(NotificationsPage) },
      { path: 'account/delete', element: page(DeleteAccountPage) },
      { path: 'past-trips', element: page(PastTripsPage) },
      { path: 'terms', element: page(TermsPage) },
      { path: 'refunds', element: page(RefundsPage) },
      { path: 'privacy', element: page(PrivacyPage) },
      { path: '*', element: page(NotFound) },
      ...(import.meta.env.DEV
        ? [{ path: 'dev/components', element: page(lazy(() => import('./dev/ComponentGallery'))) }]
        : []),
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
